import { Alert, Button, CircularProgress } from '@mui/material'
import React, { useEffect, useRef, useState } from 'react'
import { useTranslate } from 'react-admin'
import { exchangeSsoHandoff } from '../authProvider'
import AuthShell from './AuthShell'

// Only ever redirect within the SPA: a plain absolute path, no scheme, no
// protocol-relative ('//host') escape. Anything else falls back to home.
const safeNext = (raw: string | null): string => {
  if (!raw || !raw.startsWith('/') || raw.startsWith('//') || raw.includes(':')) return '/'
  return raw
}

/**
 * #/sso — OPS → vet-portal deep-link SSO landing (leg 2).
 *
 * The OPS clinic-monitoring console opens
 * `/vet-portal/#/sso?token=<handoff>&next=/pets/<id>/show`: a ~2-minute,
 * single-use handoff token minted by POST /api/users/ops-sso, riding the URL
 * FRAGMENT so it never crosses the wire or lands in an access log. This page
 * exchanges it for a normal session (the backend deletes the handoff's
 * session row — replaying the URL fails), rewrites the hash to `next` via
 * history.replaceState (the token URL doesn't survive in this tab's
 * history), and hands control to the Admin.
 *
 * MUST render INSTEAD OF the react-admin <Admin> (see the gate in App.tsx),
 * not as a CustomRoute inside it: the Admin's auth machinery runs anonymous
 * GET /me probes on boot, and when those resolve user:null it fires
 * authProvider.logout() — which clears sessionStorage and POSTs /logout with
 * whatever token is there, killing the session the exchange just stored
 * (observed live 2026-07-09: exchange 200 → logout → back on /login). With
 * the pre-Admin gate there is no auth machinery until the session exists.
 *
 * Deliberately react-admin-free (no useTranslate/useNavigate — no providers
 * exist out here). English-only copy matches the OPS console, the only
 * surface that links in.
 */
const SsoLanding: React.FC<{ onDone: () => void }> = ({ onDone }) => {
  const translate = useTranslate()
  const [failed, setFailed] = useState(false)
  // React 18 StrictMode double-mounts in dev; the handoff is single-use so
  // a second exchange would 401 and false-negative the whole flow.
  const started = useRef(false)

  useEffect(() => {
    if (started.current) return
    started.current = true

    // HashRouter-style URL: /vet-portal/#/sso?token=...&next=...
    const hashQuery = window.location.hash.split('?')[1] ?? ''
    const params = new URLSearchParams(hashQuery)
    const token = params.get('token')
    const next = safeNext(params.get('next'))

    if (!token) {
      setFailed(true)
      return
    }

    exchangeSsoHandoff(token)
      .then(() => {
        // replaceState: the /sso?token=... entry is overwritten, not kept in
        // this tab's history. The Admin then boots directly at `next`.
        window.history.replaceState(null, '', `${window.location.pathname}#${next}`)
        onDone()
      })
      .catch(() => setFailed(true))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const goToLogin = () => {
    window.history.replaceState(null, '', `${window.location.pathname}#/login`)
    onDone()
  }

  // This renders before <Admin>, so App.tsx wraps it in I18nContextProvider with the app's
  // own i18nProvider: the text follows the browser-detected locale.
  return failed ? (
    <AuthShell title={translate('pos.sso.expired_title')}>
      <Alert severity="error" sx={{ mb: 3 }} data-testid="sso-error">
        {translate('pos.sso.expired_body')}
      </Alert>
      <Button variant="contained" fullWidth sx={{ py: 1.25 }} onClick={goToLogin}>
        {translate('pos.sso.go_to_sign_in')}
      </Button>
    </AuthShell>
  ) : (
    <AuthShell icon={<CircularProgress size={36} />} title={translate('pos.sso.signing_in')} />
  )
}

export default SsoLanding
